import mime from 'mime';
import { useCallback, useEffect, useMemo } from 'react';
import type {
  Control,
  FieldValues,
  Path,
  UseFormSetValue,
} from 'react-hook-form';
import { useController } from 'react-hook-form';

import {
  FileUploadField,
  useAddFileUpload,
  useFileUpload,
} from './file-upload-field';
import type { FileUpload } from './file-upload-field/types';

type Props<T extends FieldValues> = {
  control: Control<T>;
  fieldId: string;
  path: Path<T>;
  maxSizeInBytes?: number;
  supportedTypes?: string;
  setValue: UseFormSetValue<T>;
  trackingProps: {
    context: string;
    flags: string;
    itemUrn: string;
    page: string;
  };
  setIsLoading?: (fieldId: string, isLoading: boolean) => void;
  required?: boolean;
  requiredErrorMessage?: string;
  origin?: string;
  defaultFile?: {
    key: string;
    name: string;
    url?: string;
    lastUsed?: Date;
  };
};

export const FileField = <T extends FieldValues>({
  control,
  fieldId,
  path,
  maxSizeInBytes,
  supportedTypes,
  setValue,
  setIsLoading,
  trackingProps,
  required,
  requiredErrorMessage,
  origin = 'custom_field',
  defaultFile,
}: Props<T>): React.ReactElement => {
  const fileUploaded = useFileUpload(fieldId);
  const addFile = useAddFileUpload(fieldId);
  const {
    field: { ref: fieldRef, value: fieldValue },
    fieldState: { error },
  } = useController({
    control,
    name: path,
    rules: { required: required },
  });

  const previousFile: FileUpload | undefined = useMemo(() => {
    if (fileUploaded) {
      return fileUploaded;
    }
    if (!defaultFile || fieldValue !== defaultFile.key) {
      return undefined;
    }
    return {
      file: {
        name: defaultFile.name,
        size: 0,
        type: mime.getType(defaultFile.key) ?? '',
      },
      fileServerId: defaultFile.key,
      url: defaultFile.url,
      lastUsed: defaultFile.lastUsed,
      status: 'success' as const,
      progress: 100,
    };
  }, [defaultFile, fieldValue, fileUploaded]);

  const isUploadInProgress =
    previousFile?.status === 'loading-token' ||
    previousFile?.status === 'uploading';

  useEffect(() => {
    setIsLoading?.(fieldId, isUploadInProgress);
  }, [fieldId, isUploadInProgress, setIsLoading]);

  const onFieldValueChange = useCallback(
    (value: string) => {
      // @ts-expect-error value type
      setValue(path, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [path, setValue]
  );

  const defaultErrorMessage =
    origin === 'cv'
      ? 'JOBS_INSTANT_APPLY_UPLOAD_DOCUMENTS_FIELD_ERROR'
      : 'JOBS_CUSTOM_APPLY_QUESTION_UPLOAD_FILE_EMPTY_ERROR';

  const errorMessage = requiredErrorMessage ?? defaultErrorMessage;

  return (
    <FileUploadField
      origin={origin}
      onFileChange={onFieldValueChange}
      buttonRef={fieldRef}
      maxSizeInBytes={maxSizeInBytes}
      supportedTypes={supportedTypes}
      showRequiredError={!!error && !isUploadInProgress}
      requiredErrorMessage={errorMessage}
      fileUploaded={previousFile}
      uploadFile={addFile}
      nwtProps={trackingProps}
    />
  );
};
